// FILE: client/src/actions/subcategoryActions.js

import * as S from "../constants/subcategoryConstants";
import api from "../utils/axiosInstance";
import { getApiErrorMessage } from "../utils/errorMessages";

const getErrorMessage = (error) =>
  getApiErrorMessage(error, {
    fallback:
      "We could not update subcategories right now. Please try again later.",
  });

const normalizeString = (value = "") => String(value || "").trim();

const normalizePositiveInt = (value, fallback) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const normalizeImages = (images) => {
  if (!Array.isArray(images)) return [];

  return images
    .filter((image) => image && typeof image === "object")
    .map((image) => ({
      url: normalizeString(image.url),
      public_id: normalizeString(image.public_id),
      alt: normalizeString(image.alt),
      caption: normalizeString(image.caption),
    }))
    .filter((image) => image.url);
};

const normalizeBoolean = (value, fallback = true) => {
  if (typeof value === "boolean") return value;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }

  return fallback;
};

const dispatchFailure = (dispatch, type, error) => {
  dispatch({
    type,
    payload: getErrorMessage(error),
  });
};

const buildSubcategoryListQuery = ({
  shopKey,
  parentId = "",
  keyword = "",
  pageNumber = 1,
  pageSize = 100,
  isPublished,
}) => {
  const params = new URLSearchParams();

  const safeShopKey = normalizeString(shopKey);
  const safeParentId = normalizeString(parentId);
  const safeKeyword = normalizeString(keyword);
  const safePageNumber = normalizePositiveInt(pageNumber, 1);
  const safePageSize = normalizePositiveInt(pageSize, 100);

  if (safeShopKey) params.set("shopKey", safeShopKey);
  if (safeParentId) params.set("parentId", safeParentId);
  if (safeKeyword) params.set("keyword", safeKeyword);
  if (safePageNumber) params.set("pageNumber", String(safePageNumber));
  if (safePageSize) params.set("pageSize", String(safePageSize));

  if (typeof isPublished === "boolean") {
    params.set("isPublished", String(isPublished));
  }

  return params.toString();
};

/* ----------------------------- Reset / UI helpers ----------------------------- */

export const resetSubcategoryList = () => ({
  type: S.SUBCATEGORY_LIST_RESET,
});

export const resetSubcategoryDetails = () => ({
  type: S.SUBCATEGORY_DETAILS_RESET,
});

export const resetAdminSubcategoryList = () => ({
  type: S.ADMIN_SUBCATEGORY_LIST_RESET,
});

export const resetAdminSubcategoryDetails = () => ({
  type: S.ADMIN_SUBCATEGORY_DETAILS_RESET,
});

export const resetAdminSubcategoryCreate = () => ({
  type: S.ADMIN_SUBCATEGORY_CREATE_RESET,
});

export const resetAdminSubcategoryUpdate = () => ({
  type: S.ADMIN_SUBCATEGORY_UPDATE_RESET,
});

export const resetAdminSubcategoryDelete = () => ({
  type: S.ADMIN_SUBCATEGORY_DELETE_RESET,
});

export const clearSubcategoryError = () => ({
  type: S.SUBCATEGORY_CLEAR_ERROR,
});

export const clearAdminSubcategoryError = () => ({
  type: S.ADMIN_SUBCATEGORY_CLEAR_ERROR,
});

/* ----------------------------- Public ----------------------------- */

export const listSubcategories =
  ({
    shopKey,
    parentId = "",
    keyword = "",
    pageNumber = 1,
    pageSize = 100,
  } = {}) =>
  async (dispatch) => {
    const queryParams = {
      shopKey: normalizeString(shopKey),
      parentId: normalizeString(parentId),
      keyword: normalizeString(keyword),
      pageNumber: normalizePositiveInt(pageNumber, 1),
      pageSize: normalizePositiveInt(pageSize, 100),
    };

    if (!queryParams.shopKey) {
      return dispatch({
        type: S.SUBCATEGORY_LIST_FAIL,
        payload: "shopKey is required.",
      });
    }

    try {
      dispatch({
        type: S.SUBCATEGORY_LIST_REQUEST,
        meta: queryParams,
      });

      const query = buildSubcategoryListQuery(queryParams);

      const { data } = await api.get(`/subcategories?${query}`);

      dispatch({
        type: S.SUBCATEGORY_LIST_SUCCESS,
        payload: data,
        meta: queryParams,
      });
    } catch (error) {
      dispatchFailure(dispatch, S.SUBCATEGORY_LIST_FAIL, error);
    }
  };

export const detailsSubcategory = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: S.SUBCATEGORY_DETAILS_FAIL,
      payload: "Subcategory id is required.",
    });
  }

  try {
    dispatch({
      type: S.SUBCATEGORY_DETAILS_REQUEST,
      meta: { id: safeId },
    });

    const { data } = await api.get(
      `/subcategories/${encodeURIComponent(safeId)}`,
    );

    dispatch({
      type: S.SUBCATEGORY_DETAILS_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });
  } catch (error) {
    dispatchFailure(dispatch, S.SUBCATEGORY_DETAILS_FAIL, error);
  }
};

/* ----------------------------- Admin ----------------------------- */

export const adminListSubcategories =
  ({
    shopKey,
    parentId = "",
    keyword = "",
    pageNumber = 1,
    pageSize = 100,
    isPublished,
  } = {}) =>
  async (dispatch) => {
    const queryParams = {
      shopKey: normalizeString(shopKey),
      parentId: normalizeString(parentId),
      keyword: normalizeString(keyword),
      pageNumber: normalizePositiveInt(pageNumber, 1),
      pageSize: normalizePositiveInt(pageSize, 100),
      isPublished,
    };

    if (!queryParams.shopKey) {
      const errorPayload = "shopKey is required.";

      dispatch({
        type: S.ADMIN_SUBCATEGORY_LIST_FAIL,
        payload: errorPayload,
      });

      throw new Error(errorPayload);
    }

    try {
      dispatch({
        type: S.ADMIN_SUBCATEGORY_LIST_REQUEST,
        meta: queryParams,
      });

      const query = buildSubcategoryListQuery(queryParams);

      const { data } = await api.get(`/subcategories/admin/list?${query}`);

      dispatch({
        type: S.ADMIN_SUBCATEGORY_LIST_SUCCESS,
        payload: data,
        meta: queryParams,
      });

      return data;
    } catch (error) {
      dispatchFailure(dispatch, S.ADMIN_SUBCATEGORY_LIST_FAIL, error);
      throw error;
    }
  };

export const adminGetSubcategory = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: S.ADMIN_SUBCATEGORY_DETAILS_FAIL,
      payload: "Subcategory id is required.",
    });
  }

  try {
    dispatch({
      type: S.ADMIN_SUBCATEGORY_DETAILS_REQUEST,
      meta: { id: safeId },
    });

    const { data } = await api.get(
      `/subcategories/admin/${encodeURIComponent(safeId)}`,
    );

    dispatch({
      type: S.ADMIN_SUBCATEGORY_DETAILS_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    dispatchFailure(dispatch, S.ADMIN_SUBCATEGORY_DETAILS_FAIL, error);
    throw error;
  }
};

export const adminCreateSubcategory = (subcategory) => async (dispatch) => {
  const payload = {
    shopKey: normalizeString(subcategory?.shopKey),
    parentId: normalizeString(subcategory?.parentId),
    name: normalizeString(subcategory?.name),
    slug: normalizeString(subcategory?.slug),
    images: normalizeImages(subcategory?.images),
    isPublished: normalizeBoolean(subcategory?.isPublished, true),
  };

  if (!payload.shopKey || !payload.parentId || !payload.name) {
    const errorPayload = "shopKey, parentId and name are required.";

    dispatch({
      type: S.ADMIN_SUBCATEGORY_CREATE_FAIL,
      payload: errorPayload,
    });

    throw new Error(errorPayload);
  }

  try {
    dispatch({ type: S.ADMIN_SUBCATEGORY_CREATE_REQUEST });

    const { data } = await api.post("/subcategories/admin", payload);

    dispatch({
      type: S.ADMIN_SUBCATEGORY_CREATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    dispatchFailure(dispatch, S.ADMIN_SUBCATEGORY_CREATE_FAIL, error);
    throw error;
  }
};

export const adminUpdateSubcategory = (id, subcategory) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: S.ADMIN_SUBCATEGORY_UPDATE_FAIL,
      payload: "Subcategory id is required.",
    });
  }

  const payload = {};
  const source = subcategory || {};

  if ("parentId" in source) {
    payload.parentId = normalizeString(source.parentId);
  }

  if ("name" in source) {
    payload.name = normalizeString(source.name);
  }

  if ("slug" in source) {
    payload.slug = normalizeString(source.slug);
  }

  if ("images" in source) {
    payload.images = normalizeImages(source.images);
  }

  if ("isPublished" in source) {
    payload.isPublished = normalizeBoolean(source.isPublished, true);
  }

  try {
    dispatch({
      type: S.ADMIN_SUBCATEGORY_UPDATE_REQUEST,
      meta: { id: safeId },
    });

    const { data } = await api.put(
      `/subcategories/admin/${encodeURIComponent(safeId)}`,
      payload,
    );

    dispatch({
      type: S.ADMIN_SUBCATEGORY_UPDATE_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    dispatchFailure(dispatch, S.ADMIN_SUBCATEGORY_UPDATE_FAIL, error);
    throw error;
  }
};

export const adminDeleteSubcategory = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: S.ADMIN_SUBCATEGORY_DELETE_FAIL,
      payload: "Subcategory id is required.",
    });
  }

  try {
    dispatch({
      type: S.ADMIN_SUBCATEGORY_DELETE_REQUEST,
      meta: { id: safeId },
    });

    await api.delete(`/subcategories/admin/${encodeURIComponent(safeId)}`);

    dispatch({
      type: S.ADMIN_SUBCATEGORY_DELETE_SUCCESS,
      payload: { id: safeId },
      meta: { id: safeId },
    });

    return { id: safeId };
  } catch (error) {
    dispatchFailure(dispatch, S.ADMIN_SUBCATEGORY_DELETE_FAIL, error);
    throw error;
  }
};
